import { useState, useEffect, memo } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useData } from '../context/DataContext'
import { useVehicle } from '../context/VehicleContext'
import { useLiveData } from '../context/LiveDataContext'
import { fetchSolarPrediction } from '../services/influxdb'
import { todayStr as berlinToday } from '../utils/localTime'

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api'

// ── Formatting helpers ────────────────────────────────────────────────────────

function fmtW(w) {
  if (w == null) return '–'
  const abs = Math.abs(w)
  return abs >= 1000 ? `${(abs / 1000).toFixed(1)}kW` : `${Math.round(abs)}W`
}

function fmtKwh(v) {
  if (v == null) return '–'
  return `${v.toFixed(1)}`
}

function fmtSoc(v) {
  if (v == null) return '–'
  return `${Math.round(v)}%`
}

function fmtEur(v) {
  if (v == null) return '–'
  return `${v.toFixed(2)}€`
}

// Rolling battery price, ct per kWh delivered (server/services/batteryPrice.js).
function fmtCt(v) {
  if (v == null) return '–'
  return `${v.toFixed(1)}ct`
}

function fmtKwhFull(v) {
  if (v == null) return '–'
  return `${v.toFixed(1)} kWh`
}

// ── Icon chip ─────────────────────────────────────────────────────────────────

function Chip({ icon, label, accent, segments }) {
  return (
    <span className="flex items-center gap-[3px]" style={{ color: accent ?? '#8d928a' }}>
      <span className="material-symbols-outlined" style={{ fontSize: 13, lineHeight: 1 }}>{icon}</span>
      {segments ? (
        <span style={{ fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
          {segments.map((s, i) => (
            <span key={i}>
              {i > 0 && <span style={{ color: '#3a3c3a' }}> / </span>}
              <span style={{ color: s.color }}>{s.text}</span>
            </span>
          ))}
        </span>
      ) : (
        <span style={{ fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
          {label}
        </span>
      )}
    </span>
  )
}

function Sep() {
  return <span style={{ color: '#3a3c3a', fontSize: 11, userSelect: 'none' }}>·</span>
}

// ── Modal row ─────────────────────────────────────────────────────────────────

function ModalRow({ icon, title, value, sub, accent }) {
  return (
    <div className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid #2a2c2a' }}>
      <div className="flex items-center gap-2">
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: accent ?? '#636660' }}>{icon}</span>
        <span style={{ fontSize: 12, color: '#8d928a', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</span>
      </div>
      <div className="flex items-center gap-2">
        {sub && <span style={{ fontSize: 11, color: '#636660', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{sub}</span>}
        <span style={{ fontSize: 13, color: accent ?? '#d4d8d3', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600 }}>{value}</span>
      </div>
    </div>
  )
}

// ── Detail modal ──────────────────────────────────────────────────────────────

function OverviewModal({ data, onClose }) {
  const { energyFlow, battery, solarToday, bmw, iobroker, roiToday, batteryPrice } = data

  const solar        = energyFlow?.solar     ?? null
  const grid         = energyFlow?.gridPower ?? null
  const home         = energyFlow?.home      ?? null
  const battPower    = battery?.power        ?? null
  const battSoc      = battery?.soc          ?? null
  const currentKwh   = solarToday?.actual    ?? null
  const predictedKwh = solarToday?.predicted ?? null
  const carSoc       = bmw?.soc              ?? null
  const chargerPower = iobroker?.charger?.power    ?? null
  const isCharging   = iobroker?.charger?.isCharging ?? false

  const battCharge  = battPower != null && battPower > 0
  const battLabel   = battPower != null ? `${battPower > 0 ? '+' : ''}${fmtW(Math.abs(battPower))}` : '–'

  return createPortal(
    <motion.div
      className="fixed inset-0 flex items-end sm:items-center justify-center z-[2000]"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl px-5 pt-4 pb-6"
        style={{ background: '#1b1c1b', border: '1px solid #2a2c2a', maxHeight: '80vh', overflowY: 'auto' }}
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0,  opacity: 1 }}
        exit={{   y: 40, opacity: 0 }}
        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <span style={{ fontSize: 13, color: '#636660', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Live Überblick
          </span>
          <button onClick={onClose} className="material-symbols-outlined" style={{ fontSize: 18, color: '#636660' }}>
            close
          </button>
        </div>

        <ModalRow icon="solar_power"    title="Solar"          value={fmtW(solar)}       accent="#b0ceae" />
        <ModalRow icon="today"          title="Solar heute"    value={`${fmtKwh(roiToday?.pvSavingsKwh)} / ${fmtKwh(currentKwh)} / ${fmtKwh(predictedKwh)} kWh`} sub="genutzt / ist / plan" accent="#b0ceae" />
        <ModalRow
          icon={grid != null && grid !== 0 ? (grid > 0 ? 'south_east' : 'north_west') : 'electric_meter'}
          title="Netz"
          value={grid != null && grid !== 0 ? fmtW(Math.abs(grid)) : '–'}
          accent={grid != null && grid > 0 ? '#ffb59b' : '#a8d8b0'}
        />
        <ModalRow
          icon="battery_charging_full"
          title="Hausbatterie"
          value={battLabel}
          sub={battSoc != null ? `${fmtSoc(battSoc)} SoC` : undefined}
          accent={battCharge ? '#a8d8b0' : '#ffb59b'}
        />
        {batteryPrice?.ct != null && (
          <ModalRow
            icon="sell"
            title="Akkupreis"
            value={`${batteryPrice.ct.toFixed(1)} ct/kWh`}
            sub={batteryPrice.gridShare != null ? `${Math.round((1 - batteryPrice.gridShare) * 100)} % Sonne · ${Math.round(batteryPrice.gridShare * 100)} % Nacht` : undefined}
            accent="#d4d8d3"
          />
        )}
        <ModalRow icon="home"           title="Verbrauch"      value={fmtW(home)}        accent="#d4d8d3" />
        <ModalRow
          icon="ev_station"
          title={isCharging ? 'Auto lädt' : 'Auto'}
          value={isCharging && chargerPower != null ? `${chargerPower.toFixed(1)} kW` : '–'}
          sub={[carSoc != null ? `${fmtSoc(carSoc)} SoC` : null, roiToday?.wallboxKwhToday != null ? `${fmtKwh(roiToday.wallboxKwhToday)} kWh heute` : null].filter(Boolean).join(' · ') || undefined}
          accent={isCharging ? '#c4b5fd' : '#636660'}
        />

        {roiToday && (
          <>
            <ModalRow icon="savings"   title="Einsparung heute"  value={fmtEur(roiToday.savedEur)}      accent="#fcd34d" />

            <ModalRow icon="bolt"      title="Netzkosten heute"  value={fmtEur(roiToday.importCostEur)} accent="#ffb59b" />
            <ModalRow icon="upload"    title="Einspeisung heute" value={fmtEur(roiToday.exportEur)}     accent="#93c5fd" />
          </>
        )}
      </motion.div>
    </motion.div>,
    document.body
  )
}

// ── Bar ───────────────────────────────────────────────────────────────────────

function MqttOverviewBar({ isTabletMode = false }) {
  const { iobroker, batteryPrice } = useData()
  const { bmw } = useVehicle()
  const { energyFlow, battery } = useLiveData()
  const [open, setOpen] = useState(false)
  const [roiToday, setRoiToday] = useState(null)
  const [solarToday, setSolarToday] = useState(null)

  useEffect(() => {
    const loadRoi = () => fetch(`${API_BASE}/influx/data/solar-roi`)
      .then(r => r.json())
      .then(d => setRoiToday(d.today))
      .catch(() => {})
    loadRoi()
    const onVisible = () => { if (document.visibilityState === 'visible') loadRoi() }
    document.addEventListener('visibilitychange', onVisible)
    document.addEventListener('yokan:refresh', loadRoi)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      document.removeEventListener('yokan:refresh', loadRoi)
    }
  }, [])

  useEffect(() => {
    const today = berlinToday()
    const load = () => fetchSolarPrediction(today, today)
      .then(d => setSolarToday(d.find(x => x.date === today) ?? null))
      .catch(() => {})
    load()
    const id = setInterval(load, 60_000)
    const onVisible = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', onVisible)
    document.addEventListener('yokan:refresh', load)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
      document.removeEventListener('yokan:refresh', load)
    }
  }, [])

  const solar        = energyFlow?.solar        ?? null
  const grid         = energyFlow?.gridPower     ?? null
  const home         = energyFlow?.home          ?? null
  const wallbox      = energyFlow?.wallboxPower  ?? null
  const wallboxAmp   = energyFlow?.wallboxAmp    ?? null
  const battPower    = battery?.power            ?? null
  const battSoc      = battery?.soc              ?? null
  const currentKwh   = solarToday?.actual    ?? null
  const predictedKwh = solarToday?.predicted ?? null
  const carSoc       = bmw?.soc                 ?? null

  const gridSign  = grid != null ? (grid > 0 ? '↑' : '↓') : null
  const battSign  = battPower != null ? (battPower > 0 ? '+' : '−') : null

  const data = { energyFlow, battery, solarToday, bmw, iobroker, roiToday, batteryPrice }

  return (
    <>
      <button
        id="mqtt-overview-bar"
        onClick={() => setOpen(true)}
        className={`flex flex-wrap items-center gap-x-2 gap-y-1.5 px-3 py-1.5 rounded-2xl cursor-pointer transition-colors hover:bg-[#252725] ${
          isTabletMode ? 'absolute top-[-4px] left-5 z-10 scale-90 origin-left' : 'lg:col-span-12 -mb-1'
        }`}
        style={{ background: '#1b1c1b', border: 'none' }}
      >
        {/* Solar power */}
        <Chip icon="solar_power" label={fmtW(solar)} accent="#b0ceae" />

        <Sep />

        {/* Solar today: used / actual / predicted */}
        <Chip icon="energy_savings_leaf" label={fmtKwh(roiToday?.pvSavingsKwh)} accent="#b0ceae" />
        <Chip icon="wb_sunny"            label={fmtKwh(currentKwh)}              accent="#b0ceae" />
        <Chip icon="partly_cloudy_day"   label={fmtKwh(predictedKwh)}            accent="#93c5fd" />

        <Sep />

        {/* Energie: Netz / Verbrauch / Hausbatterie */}
        <Chip
          icon={grid != null && grid !== 0 ? (grid > 0 ? 'south_east' : 'north_west') : 'electric_meter'}
          label={grid != null ? gridSign + fmtW(Math.abs(grid)) : '–'}
          accent={grid == null ? '#8d928a' : grid > 0 ? '#ffb59b' : '#a8d8b0'}
        />
        <Chip icon="home"                   label={fmtW(home)}                                              accent="#d4d8d3" />
        <Chip icon="battery_charging_full"  label={`${battSign ?? ''}${fmtW(battPower)}·${fmtSoc(battSoc)}`} accent={battPower != null && battPower > 0 ? '#a8d8b0' : '#ffb59b'} />
        {batteryPrice?.ct != null && <Chip icon="sell" label={fmtCt(batteryPrice.ct)} accent="#d4d8d3" />}

        <Sep />

        {/* Car charging */}
        <Chip
          icon="ev_station"
          label={wallbox != null && wallbox > 0
            ? `${fmtW(wallbox)}${wallboxAmp != null ? ` (${Math.round(wallboxAmp)}A)` : ''}${carSoc != null ? `·${fmtSoc(carSoc)}` : ''}${roiToday?.wallboxKwhToday != null ? `·${fmtKwh(roiToday.wallboxKwhToday)}kWh` : ''}`
            : `–${carSoc != null ? `·${fmtSoc(carSoc)}` : ''}${roiToday?.wallboxKwhToday != null ? `·${fmtKwh(roiToday.wallboxKwhToday)}kWh` : ''}`}
          accent="#c4b5fd"
        />

        {roiToday && (
          <>
            <Sep />
            <Chip icon="savings"  label={fmtEur(roiToday.savedEur)}          accent="#fcd34d" />
            <Chip icon="bolt"     label={fmtEur(roiToday.importCostEur)}      accent="#ffb59b" />
            <Chip icon="upload"   label={fmtEur(roiToday.exportEur)}          accent="#93c5fd" />
          </>
        )}
      </button>

      <AnimatePresence>
        {open && <OverviewModal data={data} onClose={() => setOpen(false)} />}
      </AnimatePresence>
    </>
  )
}

export default memo(MqttOverviewBar)
